import { useEffect, useState } from 'react';

const find = (anchor: string) => document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);

/**
 * The element marked `data-tour={anchor}`, tracked as it mounts and unmounts.
 * DOM changes are checked at most once per frame.
 */
export function useTourAnchor(anchor: string | undefined): HTMLElement | null {
  const [element, setElement] = useState<HTMLElement | null>(() => (anchor ? find(anchor) : null));

  useEffect(() => {
    if (!anchor) {
      setElement(null);
      return;
    }
    const sync = () => {
      const next = find(anchor);
      setElement((current) => (current === next ? current : next));
    };
    let frame: number | null = null;
    const schedule = () => {
      if (frame === null)
        frame = requestAnimationFrame(() => {
          frame = null;
          sync();
        });
    };
    sync();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [anchor]);

  return element;
}
